import * as THREE from 'three';
import gsap from 'gsap';
import characterVoxels1 from './characterData.json';
import characterVoxels2 from './characterData2.json';

export default class Character {
  constructor(experience) {
    this.experience = experience;
    this.scene = this.experience.scene;

    this.container = new THREE.Group();
    this.container.position.set(0, -0.1, 0);
    this.scene.add(this.container);

    this.baseY = this.container.position.y;
    this.isAnimating = false;
    this.interactiveMeshes = [];
    this.activeCharacterIndex = 2; // 0: Mullet Guy, 1: Suit & Moustache, 2: Patch Lady (Default active)

    // Shared material for high-definition tactile physical sheen with crisp definition
    this.material = new THREE.MeshPhysicalMaterial({
      roughness: 0.38,
      metalness: 0.04,
      clearcoat: 0.22,
      clearcoatRoughness: 0.18,
      reflectivity: 0.5
    });

    this.characters = [];
    this.setupCharacters();
    this.createHitBox();
  }

  setupCharacters() {
    // 1. Build Character 1 (Mullet & Sunglasses)
    const char1Group = this.buildVoxelMesh(characterVoxels1, 0.144, 0);
    char1Group.visible = false;
    char1Group.scale.set(0, 0, 0);
    this.container.add(char1Group);
    this.characters.push(char1Group);

    // 2. Build Character 2 (Suit & Moustache) - 43x43 high resolution
    const char2Group = this.buildVoxelMesh(characterVoxels2, 0.088, 1);
    char2Group.visible = false;
    char2Group.scale.set(0, 0, 0);
    this.container.add(char2Group);
    this.characters.push(char2Group);

    // 3. Build Character 3 (Authentic 2D Patch Cutout in 3D Space)
    const char3Group = this.buildCutoutMesh('/models/patch_cutout.png');
    char3Group.visible = true;
    char3Group.scale.set(1, 1, 1);
    this.container.add(char3Group);
    this.characters.push(char3Group);

    this.currentMeshGroup = this.characters[2];
  }

  buildCutoutMesh(texturePath) {
    const group = new THREE.Group();
    const loader = new THREE.TextureLoader();
    const texture = loader.load(texturePath);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.generateMipmaps = true;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;

    const geometry = new THREE.PlaneGeometry(4.4, 4.4);

    // Front illuminated physical patch material
    const material = new THREE.MeshStandardMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.05,
      roughness: 0.6,
      metalness: 0.02,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(0, 0.05, 0);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    mesh.customDepthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
      map: texture,
      alphaTest: 0.05
    });

    group.add(mesh);

    // Subtle physical back shadow layer for floating depth separation
    const shadowMat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      color: 0x000000,
      opacity: 0.28,
      alphaTest: 0.05,
      side: THREE.DoubleSide
    });
    const shadowMesh = new THREE.Mesh(geometry, shadowMat);
    shadowMesh.position.set(0.03, -0.03, -0.05);
    shadowMesh.scale.set(0.99, 0.99, 1);
    group.add(shadowMesh);

    return group;
  }

  buildVoxelMesh(voxelData, voxelSize, characterIndex) {
    const group = new THREE.Group();
    const count = voxelData.length;
    const geometry = new THREE.BoxGeometry(voxelSize, voxelSize, voxelSize);

    const instancedMesh = new THREE.InstancedMesh(geometry, this.material, count);
    instancedMesh.castShadow = true;
    instancedMesh.receiveShadow = true;

    const dummy = new THREE.Object3D();
    const color = new THREE.Color();

    voxelData.forEach((v, index) => {
      dummy.position.set(v.x, v.y, v.z);

      // Character-specific layer pop for enhanced 3D definition
      if (characterIndex === 0) {
        if (v.type === 'glasses') {
          dummy.scale.set(1.06, 1.06, 1.35);
        } else if (v.type === 'button') {
          dummy.scale.set(1.12, 1.12, 1.45);
        } else {
          dummy.scale.set(1, 1, 1);
        }
      } else if (characterIndex === 1) {
        // Character 2 (Glasses, Lenses, Moustache, Tie)
        if (v.type === 'glasses') {
          dummy.scale.set(1.08, 1.08, 1.35);
        } else if (v.type === 'lens') {
          dummy.scale.set(1.02, 1.02, 1.25);
        } else if (v.type === 'moustache') {
          dummy.scale.set(1.1, 1.1, 1.3);
        } else if (v.type === 'tie') {
          dummy.scale.set(1.05, 1.05, 1.2);
        } else {
          dummy.scale.set(1, 1, 1);
        }
      } else if (characterIndex === 2) {
        // Character 3 (Tortoise Glasses, Eyes, Lips, Teeth, Collar, Pendant)
        if (v.type === 'glasses') {
          dummy.scale.set(1.08, 1.08, 1.42);
        } else if (v.type === 'eye') {
          dummy.scale.set(1.02, 1.02, 1.15);
        } else if (v.type === 'lips') {
          dummy.scale.set(1.06, 1.06, 1.28);
        } else if (v.type === 'teeth') {
          dummy.scale.set(1.02, 1.02, 1.16);
        } else if (v.type === 'pendant') {
          dummy.scale.set(1.08, 1.08, 1.32);
        } else if (v.type === 'collar' || v.type === 'embroidery_gold') {
          dummy.scale.set(1.04, 1.04, 1.22);
        } else {
          dummy.scale.set(1, 1, 1);
        }
      }

      dummy.updateMatrix();
      instancedMesh.setMatrixAt(index, dummy.matrix);

      color.set(v.hex);
      instancedMesh.setColorAt(index, color);
    });

    instancedMesh.instanceMatrix.needsUpdate = true;
    if (instancedMesh.instanceColor) {
      instancedMesh.instanceColor.needsUpdate = true;
    }

    group.add(instancedMesh);
    return group;
  }

  createHitBox() {
    const hitGeo = new THREE.BoxGeometry(3.2, 4.2, 1.8);
    const hitMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false
    });

    this.hitBox = new THREE.Mesh(hitGeo, hitMat);
    this.container.add(this.hitBox);
    this.interactiveMeshes.push(this.hitBox);
  }

  switchCharacter(newIndex) {
    if (newIndex === this.activeCharacterIndex || this.isAnimating) return;
    this.isAnimating = true;

    const oldMesh = this.characters[this.activeCharacterIndex];
    const newMesh = this.characters[newIndex];

    const tl = gsap.timeline({
      onComplete: () => {
        this.activeCharacterIndex = newIndex;
        this.currentMeshGroup = newMesh;
        this.isAnimating = false;
      }
    });

    // 1. Current character spins out and shrinks
    tl.to(oldMesh.scale, {
      x: 0,
      y: 0,
      z: 0,
      duration: 0.28,
      ease: 'back.in(1.2)'
    }, 'exit')
    .to(oldMesh.rotation, {
      y: `+=${Math.PI}`,
      duration: 0.28,
      ease: 'power2.in'
    }, 'exit');

    // 2. Safely swap meshes and reset transformations using native Three.js setters
    tl.call(() => {
      oldMesh.visible = false;
      oldMesh.scale.set(0, 0, 0);

      newMesh.visible = true;
      newMesh.scale.set(0, 0, 0);
      newMesh.position.set(0, 0.8, 0);
      newMesh.rotation.set(0, -Math.PI, 0);
    });

    // 3. Drop in with elastic bounce and spin
    tl.to(newMesh.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.65,
      ease: 'elastic.out(1, 0.45)'
    }, 'enter')
    .to(newMesh.position, {
      y: 0,
      duration: 0.55,
      ease: 'bounce.out'
    }, 'enter')
    .to(newMesh.rotation, {
      y: 0,
      duration: 0.6,
      ease: 'power2.out'
    }, 'enter');
  }

  setHover(isHovering) {
    if (this.isAnimating) return;

    gsap.to(this.container.scale, {
      x: isHovering ? 1.05 : 1.0,
      y: isHovering ? 1.05 : 1.0,
      z: isHovering ? 1.05 : 1.0,
      duration: 0.28,
      ease: 'power2.out'
    });
  }

  triggerReaction() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    const activeMesh = this.characters[this.activeCharacterIndex];

    const tl = gsap.timeline({
      onComplete: () => {
        this.isAnimating = false;
        this.container.position.y = this.baseY;
      }
    });

    // 1. Squash
    tl.to(this.container.scale, {
      y: 0.82,
      x: 1.12,
      z: 1.12,
      duration: 0.12,
      ease: 'power1.in'
    })
    // 2. Launch up with 360 spin
    .to(this.container.position, {
      y: this.baseY + 0.75,
      duration: 0.4,
      ease: 'power2.out'
    }, 'launch')
    .to(this.container.scale, {
      y: 1.18,
      x: 0.9,
      z: 0.9,
      duration: 0.25,
      ease: 'power1.out'
    }, 'launch')
    .to(activeMesh.rotation, {
      y: `+=${Math.PI * 2}`,
      duration: 0.85,
      ease: 'back.out(1.2)'
    }, 'launch')
    // 3. Fall back down with impact bounce
    .to(this.container.position, {
      y: this.baseY,
      duration: 0.42,
      ease: 'bounce.out'
    }, 'landing')
    .to(this.container.scale, {
      x: 1.0,
      y: 1.0,
      z: 1.0,
      duration: 0.35,
      ease: 'elastic.out(1.2, 0.4)'
    }, 'landing+=0.1');
  }

  update(elapsed) {
    // Idle breathing & floating
    if (!this.isAnimating) {
      this.container.position.y = this.baseY + Math.sin(elapsed * 2.0) * 0.05;
    }

    // Dynamic LookAt following mouse
    if (this.experience.mouse && !this.isAnimating) {
      const activeMesh = this.characters[this.activeCharacterIndex];
      const targetRotY = this.experience.mouse.current.x * 0.45;
      const targetRotX = -this.experience.mouse.current.y * 0.3;

      activeMesh.rotation.y += (targetRotY - activeMesh.rotation.y) * 0.08;
      activeMesh.rotation.x += (targetRotX - activeMesh.rotation.x) * 0.08;
      activeMesh.rotation.z = -this.experience.mouse.current.x * 0.06;
    }
  }
}
